import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { ChatType } from '../../models/chat-interface';

@Component({
  selector: 'chat-input',
  template: `
    <div class="input-container">
      <div class="textarea-wrapper">
        <textarea
          matInput
          [(ngModel)]="value"
          [placeholder]="placeholder"
          (keyup.enter)="handleKeyUp($event)"
          (ngModelChange)="onValueChange($event)"
          [rows]="rows"
          class="no-resize"
          [disabled]="disabled"
        ></textarea>
      </div>

      <div class="input-actions">
        <!-- Chat Type Selector (optional) -->
        <button
          mat-icon-button
          [matMenuTriggerFor]="chatTypeMenu"
          [matTooltip]="getCurrentChatTypeLabel()"
          class="chat-type-button"
          *ngIf="showChatTypeSelector"
        >
          <mat-icon>{{ getChatTypeIcon() }}</mat-icon>
        </button>

        <mat-menu #chatTypeMenu="matMenu" class="chat-type-menu">
          <button
            mat-menu-item
            *ngFor="let chatType of availableChatTypes"
            (click)="onChatTypeChange(chatType.value)"
            [class.active]="selectedChatType === chatType.value"
          >
            <mat-icon>{{ getChatTypeIcon(chatType.value) }}</mat-icon>
            <div class="chat-option-content">
              <span class="option-label">{{ chatType.label }}</span>
              <span class="option-description">{{ chatType.description }}</span>
            </div>
          </button>
        </mat-menu>

        <button
          mat-icon-button
          (click)="onReset()"
          [matTooltip]="resetTooltip"
          class="reset-button"
          [disabled]="disabled"
          *ngIf="showResetButton && hasConversation"
        >
          <mat-icon>restart_alt</mat-icon>
        </button>

        <!-- Botón Send/Stop -->
        <button
          mat-icon-button
          (click)="isProcessing ? onStop() : onSend()"
          class="send-button"
          [class.stop-button]="isProcessing"
          [disabled]="!isProcessing && (disabled || !value?.trim())"
          [matTooltip]="isProcessing ? ('AI_CHAT.CHAT_INPUT.STOP_TOOLTIP' | pTranslate) : ('AI_CHAT.CHAT_INPUT.SEND_TOOLTIP' | pTranslate)"
        >
          <mat-icon>{{ isProcessing ? 'stop' : 'send' }}</mat-icon>
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./chat-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChatInputComponent {
  @Input() value = '';
  @Input() placeholder = '';
  @Input() rows = 2;
  @Input() disabled = false;
  @Input() isProcessing = false; // Nueva propiedad para indicar si está procesando
  @Input() showResetButton = true;
  @Input() hasConversation = false; // Nueva propiedad para controlar si hay conversación
  @Input() resetTooltip = '';
  @Input() sendOnEnter = true;
  @Input() allowShiftEnter = true;
  @Input() showChatTypeSelector = false; // Nueva opción para mostrar selector
  @Input() selectedChatType: ChatType = ChatType.DATABRICKS; // Tipo actual
  @Input() availableChatTypes: { value: ChatType; label: string; description: string }[] = [];

  @Output() valueChange = new EventEmitter<string>();
  @Output() send = new EventEmitter<string>();
  @Output() stop = new EventEmitter<void>(); // Nuevo evento para detener procesamiento
  @Output() reset = new EventEmitter<void>();
  @Output() keyUp = new EventEmitter<KeyboardEvent>();
  @Output() chatTypeChange = new EventEmitter<ChatType>(); // Nuevo evento para cambio de tipo

  constructor(private translate: IanTranslateService) {}

  onValueChange(value: string): void {
    this.value = value;
    this.valueChange.emit(value);
  }

  onSend(): void {
    if (this.value?.trim() && !this.disabled) {
      this.send.emit(this.value.trim());
    }
  }

  onStop(): void {
    this.stop.emit();
  }

  onReset() {
    this.reset.emit();
  }

  onChatTypeChange(newType: ChatType) {
    this.chatTypeChange.emit(newType);
  }

  getChatTypeIcon(type?: ChatType): string {
    const chatType = type || this.selectedChatType;
    switch (chatType) {
      case ChatType.DATABRICKS:
        return 'analytics';
      case ChatType.STREAMING:
        return 'stream';
      default:
        return 'chat';
    }
  }

  getCurrentChatTypeLabel(): string {
    const currentType = this.availableChatTypes.find(t => t.value === this.selectedChatType);
    return currentType
      ? `${this.translate.instant('AI_CHAT.CHAT_INPUT.TYPE_PREFIX')} ${currentType.label}`
      : this.translate.instant('AI_CHAT.CHAT_INPUT.SELECT_TYPE');
  }

  handleKeyUp(event: KeyboardEvent): void {
    this.keyUp.emit(event);

    if (this.sendOnEnter && event.key === 'Enter') {
      // Si allowShiftEnter es true y se presiona Shift+Enter, no enviar
      if (this.allowShiftEnter && event.shiftKey) {
        return;
      }

      // Prevenir el comportamiento por defecto y enviar mensaje
      event.preventDefault();
      this.onSend();
    }
  }
}
